import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpenCheck, Check, ChevronRight, ClipboardCheck, Globe2, SlidersHorizontal, WalletCards } from "lucide-react";
import { useTema } from "../theme/ThemeProvider";
import { useAuth } from "../auth/AuthContext";
import { Popover } from "../components/ui/Popover";
import { IconeConfiguracoes } from "../components/icons";
import { useIdioma, OPCOES_IDIOMA, type Idioma } from "../i18n/IdiomaProvider";
import { usuarioDonoPlataforma, usuarioPodeAcessarConfiguracoes } from "../utils/permissoes";
import { useAcessoTela } from "../components/ui/AcessoTela";
import { urlAsset } from "../utils/assets";
import { obterDadosPerfilUsuario, type DadosPerfilUsuario } from "../api/usuario";
import { atualizarDisponibilidadeSuporte, obterDisponibilidadeSuporte } from "../api/agenteIa";
import { mostrarToastErro } from "../utils/toast";
import { AvatarPerfilModal } from "./AvatarPerfilModal";
import { PreferenciasNotificacoesModal } from "./PreferenciasNotificacoesModal";
import { MinhaContaModal } from "./MinhaContaModal";
import { ROTAS_RECURSOS_PLATAFORMA } from "./telasDisponiveis";
import styles from "./MenuUsuario.module.css";

const IcoEditar = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
  </svg>
);
const IcoLog = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 4v4h4M12 8v4l3 2" />
  </svg>
);
const IcoAjuda = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M9.5 9a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3.5" />
    <circle cx="12" cy="17" r=".6" fill="currentColor" />
  </svg>
);
const IcoConfig = () => <IconeConfiguracoes width={16} height={16} strokeWidth={1.8} />;
const IcoBlocoNotas = () => <BookOpenCheck size={16} strokeWidth={1.7} aria-hidden="true" />;
const IcoProtocolo = () => <ClipboardCheck size={16} strokeWidth={1.7} aria-hidden="true" />;
const IcoConfigNotificacao = () => <SlidersHorizontal size={16} strokeWidth={1.7} aria-hidden="true" />;
const IcoChevron = ({ aberto = false }: { aberto?: boolean }) => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ transform: aberto ? "rotate(180deg)" : "none" }}>
    <path d="m4 6 4 4 4-4" />
  </svg>
);
const IcoLua = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 5h4M20 3v4" />
    <path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401" />
  </svg>
);
const IcoSol = () => (
  <svg viewBox="0 0 20 20" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="10" cy="10" r="3.2" />
    <path d="M10 1.8v2M10 16.2v2M18.2 10h-2M3.8 10h-2M15.8 4.2l-1.4 1.4M5.6 14.4l-1.4 1.4M15.8 15.8l-1.4-1.4M5.6 5.6 4.2 4.2" />
  </svg>
);

type StatusKey = "available" | "away" | "busy" | "offline";
const STATUS_API: Record<StatusKey, "DISPONIVEL" | "AUSENTE" | "OCUPADO" | "OFFLINE"> = {
  available: "DISPONIVEL",
  away: "AUSENTE",
  busy: "OCUPADO",
  offline: "OFFLINE",
};
const STATUS_TELA: Record<string, StatusKey> = {
  DISPONIVEL: "available",
  AUSENTE: "away",
  OCUPADO: "busy",
  OFFLINE: "offline",
};
const STATUS_OPCOES: Array<{ key: StatusKey; cor: string; descricao: string }> = [
  { key: "available", cor: "#22c55e", descricao: "Ativo e disponível para contato" },
  { key: "away", cor: "#facc15", descricao: "Estou ausente no momento" },
  { key: "busy", cor: "#f97316", descricao: "Em uma atividade intensa" },
  { key: "offline", cor: "#9ca3af", descricao: "Desconectado ou indisponível" },
];
const IDIOMA_FLAGS: Record<Idioma, string> = { pt: "🇧🇷", es: "🇪🇸", en: "🇺🇸", "zh-CN": "🇨🇳" };
const IDIOMA_META: Record<Idioma, { sigla: string; descricao: string }> = {
  pt: { sigla: "BR", descricao: "Português do Brasil" },
  es: { sigla: "ES", descricao: "Espanhol" },
  en: { sigla: "US", descricao: "Inglês" },
  "zh-CN": { sigla: "中文", descricao: "Chinês Simplificado" },
};

function nomeUsuario(email?: string, fallback = "Usuário"): string {
  if (!email) return fallback;
  const base = email.split("@")[0] || email;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

export function MenuUsuario() {
  const ancoraRef = useRef<HTMLButtonElement>(null);
  const statusRef = useRef<HTMLButtonElement>(null);
  const idiomaRef = useRef<HTMLButtonElement>(null);
  const [aberto, setAberto] = useState(false);
  const [modalPerfilAberto, setModalPerfilAberto] = useState(false);
  const [modalNotificacoesAberto, setModalNotificacoesAberto] = useState(false);
  const [modalMinhaContaAberto, setModalMinhaContaAberto] = useState(false);
  const [statusAberto, setStatusAberto] = useState(false);
  const [idiomaAberto, setIdiomaAberto] = useState(false);
  const [status, setStatus] = useState<StatusKey>("offline");
  const [perfil, setPerfil] = useState<DadosPerfilUsuario | null>(null);
  const { tema, alternarTema } = useTema();
  const { idioma, setIdioma, t, traduzir } = useIdioma();
  const { usuario, sair, recarregarSessao } = useAuth();
  const { podeAbrirTela, telaControlada, avisoSemPermissao } = useAcessoTela();
  const navegar = useNavigate();

  const nome = perfil?.nomePerfil?.trim() || usuario?.nomePerfil?.trim() || nomeUsuario(usuario?.email, t("user"));
  const papel = usuario?.acessoGlobal ? t("support") : t("user");
  const inicial = (nome[0] || "U").toUpperCase();
  const idiomaAtual = OPCOES_IDIOMA.find((item) => item.code === idioma) || OPCOES_IDIOMA[0];
  const avatarUrl = perfil?.urlImagem || urlAsset(usuario?.avatarId || "");
  const statusAtual = STATUS_OPCOES.find((item) => item.key === status) || STATUS_OPCOES[3];
  const donoPlataforma = usuarioDonoPlataforma(usuario);
  const podeAcessarConfiguracoes = usuarioPodeAcessarConfiguracoes(usuario);
  const podeConfigurarNotificacoes = podeAbrirTela(ROTAS_RECURSOS_PLATAFORMA.preferenciasNotificacoes);
  const podeAbrirMinhaConta = podeAbrirTela(ROTAS_RECURSOS_PLATAFORMA.minhaConta);

  useEffect(() => {
    if (!aberto || !usuario) return;
    let ativo = true;
    void obterDadosPerfilUsuario()
      .then((dados) => { if (ativo) setPerfil(dados); })
      .catch(() => { if (ativo) setPerfil(null); });
    return () => { ativo = false; };
  }, [aberto, usuario]);

  useEffect(() => {
    if (!usuario || ["CLIENTE", "CLIENT"].includes(String(usuario.tipoUsuario || "").toUpperCase())) return;
    void obterDisponibilidadeSuporte()
      .then((dados) => setStatus(STATUS_TELA[dados.status] || "offline"))
      .catch(() => setStatus(STATUS_TELA[String(usuario.statusAtendimento || "OFFLINE")] || "offline"));
  }, [usuario]);

  useEffect(() => {
    if (!aberto) {
      setStatusAberto(false);
      setIdiomaAberto(false);
    }
  }, [aberto]);

  useEffect(() => {
    const abrirPerfil = () => setAberto(true);
    const fecharPerfil = () => setAberto(false);
    window.addEventListener("system-tour-open-profile", abrirPerfil);
    window.addEventListener("system-tour-close-profile", fecharPerfil);
    return () => {
      window.removeEventListener("system-tour-open-profile", abrirPerfil);
      window.removeEventListener("system-tour-close-profile", fecharPerfil);
    };
  }, []);

  function ir(rota: string) {
    setAberto(false);
    navegar(rota);
  }

  function itemTela(rota: string, icone: ReactNode, rotulo: string, sempreVisivel = false) {
    if (!sempreVisivel && !telaControlada(rota)) return null;
    const liberada = sempreVisivel || podeAbrirTela(rota);
    return (
      <button key={rota} type="button" className={`${styles.item} ${!liberada ? "tooltip" : ""}`} disabled={!liberada} data-tooltip={!liberada ? avisoSemPermissao : undefined} aria-label={!liberada ? `${traduzir(rotulo)}: ${avisoSemPermissao}` : undefined} onClick={() => ir(rota)}>
        {icone}
        <span className={styles.itemTexto}>{traduzir(rotulo)}</span>
      </button>
    );
  }

  async function sincronizarPerfil() {
    try {
      setPerfil(await obterDadosPerfilUsuario());
    } catch {
      setPerfil(null);
    }
    await recarregarSessao();
  }

  return (
    <>
      <button
        ref={ancoraRef}
        type="button"
        data-tour="header-profile"
        className={`${styles.avatarBtn} ${styles.avatarBtnComIdentidade}`}
        onClick={() => setAberto((valor) => !valor)}
        aria-label={traduzir("Menu do usuário")}
        aria-expanded={aberto}
      >
        <span className={styles.avatarBorder}>
          <span className={styles.avatarWrap}>
            <span className={styles.avatar}>{avatarUrl ? <img src={avatarUrl} alt="" /> : inicial}</span>
          </span>
        </span>
        <span className={styles.pontoOnline} style={{ background: statusAtual.cor }} />
        <span className={styles.identidadeTopo}>
          <span className={styles.identidadeTopoNome}>{nome}</span>
          <span className={styles.identidadeTopoEmail}>{perfil?.emailPerfil || usuario?.email}</span>
        </span>
      </button>

      <Popover ancora={ancoraRef} aberto={aberto} onFechar={() => setAberto(false)} largura={272} alinhamento="fim">
        <div className={styles.menu} data-tour="profile-panel">
          <div className={styles.cabecalho}>
            <div className={styles.cabecalhoTextos}>
              <div className={styles.nomeLinha}>
                <span className={styles.nome}>{nome}</span>
                <button type="button" className={styles.editarNome} aria-label={traduzir("Editar perfil")} onClick={() => { setAberto(false); setModalPerfilAberto(true); }}>
                  <IcoEditar />
                </button>
              </div>
              <div className={styles.email}>{perfil?.emailPerfil || usuario?.email}</div>
              <div className={styles.papel}>{papel}</div>
            </div>
            <div className={`${styles.divisor} ${styles.divisorCabecalho}`} />
          </div>

          <div className={styles.linhaControle}>
            <span className={styles.linhaTitulo}>{t("darkMode")}</span>
            <button type="button" className={`${styles.switchLegado} ${tema === "white" ? styles.switchLegadoClaro : ""}`} onClick={alternarTema} role="switch" aria-checked={tema === "black"} aria-label={t("darkMode")}>
              <span className={styles.switchKnob} />
              <span className={`${styles.switchIcone} ${styles.switchSol}`}><IcoSol /></span>
              <span className={`${styles.switchIcone} ${styles.switchLua}`}><IcoLua /></span>
            </button>
          </div>

          <div className={styles.linhaControle}>
            <span className={styles.linhaTextos}>
              <span className={styles.linhaTituloForte}>{t("status")}</span>
              <span className={styles.linhaSubtitulo}>{traduzir("Defina seu status atual")}</span>
            </span>
            <button ref={statusRef} type="button" className={`${styles.pillLegado} ${styles.pillStatus} ${statusAberto ? styles.pillLegadoAtivo : ""}`} onClick={() => { setStatusAberto((valor) => !valor); setIdiomaAberto(false); }}>
              <span className={styles.pontoStatus} style={{ background: statusAtual.cor }} />
              <span className={styles.pillTexto}>{t(statusAtual.key)}</span>
              <IcoChevron aberto={statusAberto} />
            </button>
          </div>

          <div className={styles.linhaControle}>
            <span className={styles.linhaTextos}>
              <span className={styles.linhaTituloForte}>{t("language")}</span>
              <span className={styles.linhaSubtitulo}>{traduzir("Defina o idioma da plataforma")}</span>
            </span>
            <button ref={idiomaRef} type="button" className={`${styles.pillLegado} ${styles.pillIdioma} ${idiomaAberto ? styles.pillLegadoAtivo : ""}`} onClick={() => { setIdiomaAberto((valor) => !valor); setStatusAberto(false); }}>
              <span className={styles.flag}>{IDIOMA_FLAGS[idiomaAtual.code]}</span>
              <span className={styles.pillTexto}>{idiomaAtual.title}</span>
              <IcoChevron aberto={idiomaAberto} />
            </button>
          </div>

          <div className={styles.divisor} />
          <button type="button" className={`${styles.item} ${!podeConfigurarNotificacoes ? "tooltip" : ""}`} disabled={!podeConfigurarNotificacoes} data-tooltip={!podeConfigurarNotificacoes ? avisoSemPermissao : undefined} onClick={() => { setAberto(false); setModalNotificacoesAberto(true); }}>
            <IcoConfigNotificacao />
            <span className={styles.itemTexto}>{t("notifications")}</span>
          </button>
          <div className={styles.divisor} />

          <button type="button" className={`${styles.item} ${!podeAbrirMinhaConta ? "tooltip" : ""}`} disabled={!podeAbrirMinhaConta} data-tooltip={!podeAbrirMinhaConta ? avisoSemPermissao : undefined} onClick={() => { setAberto(false); setModalMinhaContaAberto(true); }}>
            <WalletCards aria-hidden="true" />
            <span className={styles.itemTexto}>{t("myAccount")}</span>
          </button>

          {/* O dono do SaaS sempre acessa a auditoria, mesmo quando a tela não
              consta nos módulos/perfis da organização atualmente selecionada. */}
          {itemTela("/auditoria/log-acesso", <IcoLog />, "Log de Acesso", donoPlataforma)}
          {itemTela("/bloco-notas", <IcoBlocoNotas />, "Bloco de Notas", true)}
          {itemTela("/protocolo", <IcoProtocolo />, "Protocolo")}
          {itemTela("/ajuda", <IcoAjuda />, "Ajuda")}
          {donoPlataforma && (
            <button type="button" className={`${styles.item} ${!podeAcessarConfiguracoes || !podeAbrirTela("/configuracoes") ? "tooltip" : ""}`} onClick={() => ir("/configuracoes")} disabled={!podeAcessarConfiguracoes || !podeAbrirTela("/configuracoes")} data-tooltip={podeAcessarConfiguracoes && podeAbrirTela("/configuracoes") ? undefined : avisoSemPermissao} aria-label={podeAcessarConfiguracoes && podeAbrirTela("/configuracoes") ? t("settings") : `${t("settings")}: ${avisoSemPermissao}`}>
              <IcoConfig />
              <span className={styles.itemTexto}>{t("settings")}</span>
            </button>
          )}

          <button type="button" className={styles.sair} onClick={() => void sair()}>{t("exit")}</button>
        </div>
      </Popover>

      <Popover ancora={statusRef} aberto={aberto && statusAberto} onFechar={() => setStatusAberto(false)} largura={272} alinhamento="fim">
        <div className={styles.statusLista}>
          {STATUS_OPCOES.map((opcao) => {
            const selecionado = status === opcao.key;
            const somenteSistema = opcao.key === "offline";
            return (
              <button key={opcao.key} type="button" aria-pressed={selecionado} disabled={somenteSistema} data-tooltip={somenteSistema ? traduzir("Definido automaticamente ao sair da plataforma") : undefined} aria-label={somenteSistema ? traduzir("Offline: definido automaticamente ao sair da plataforma") : undefined} className={`${styles.statusItem} ${selecionado ? styles.statusItemAtivo : ""} ${somenteSistema ? `${styles.statusItemDesabilitado} tooltip` : ""}`} onClick={() => {
                if (somenteSistema) return;
                setStatusAberto(false);
                void atualizarDisponibilidadeSuporte(STATUS_API[opcao.key]).then(() => setStatus(opcao.key)).catch((erro) => mostrarToastErro(erro instanceof Error ? erro.message : traduzir("Não foi possível alterar a disponibilidade.")));
              }}>
                <span className={styles.pontoStatusGrande} style={{ background: opcao.cor }} />
                <span className={styles.statusTextos}>
                  <span className={styles.statusTitulo}>{t(opcao.key)}</span>
                  <span className={styles.statusDescricao}>{traduzir(opcao.descricao)}</span>
                </span>
                {selecionado && <span className={styles.statusCheck} aria-hidden="true"><Check /></span>}
              </button>
            );
          })}
        </div>
      </Popover>

      <Popover ancora={idiomaRef} aberto={aberto && idiomaAberto} onFechar={() => setIdiomaAberto(false)} largura={272} alinhamento="fim">
        <div className={styles.idiomaPopover}>
          <div className={styles.idiomaCabecalho}>
            <Globe2 className={styles.idiomaIcone} size={24} strokeWidth={1.65} aria-hidden="true" />
            <span className={styles.idiomaCabecalhoTextos}>
              <span className={styles.idiomaTitulo}>{t("language")}</span>
              <span className={styles.idiomaDescricao}>{traduzir("Selecione o idioma de sua preferência.")}</span>
            </span>
          </div>
          <div className={styles.idiomaDivisor} />
          <div className={styles.idiomaLista}>
            {OPCOES_IDIOMA.map((opcao) => {
              const meta = IDIOMA_META[opcao.code];
              const selecionado = idioma === opcao.code;
              return (
                <button key={opcao.code} type="button" aria-pressed={selecionado} className={`${styles.idiomaItem} ${selecionado ? styles.idiomaItemAtivo : ""}`} onClick={() => { setIdioma(opcao.code); setIdiomaAberto(false); }}>
                  <span className={styles.idiomaSigla} aria-hidden="true">{meta.sigla}</span>
                  <span className={styles.idiomaItemTextos}>
                    <span className={styles.idiomaItemTitulo}>{opcao.title}</span>
                    <span className={styles.idiomaItemDescricao}>{traduzir(meta.descricao)}</span>
                  </span>
                  <span className={styles.idiomaIndicador} aria-hidden="true">{selecionado ? <Check size={16} strokeWidth={2} /> : <ChevronRight size={16} strokeWidth={1.6} />}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Popover>

      <AvatarPerfilModal aberto={modalPerfilAberto} onFechar={() => setModalPerfilAberto(false)} fallbackNome={nome} avatarSessao={usuario?.avatarId} onAtualizado={sincronizarPerfil} />
      <PreferenciasNotificacoesModal aberto={modalNotificacoesAberto} onFechar={() => setModalNotificacoesAberto(false)} />
      <MinhaContaModal aberto={modalMinhaContaAberto} onFechar={() => setModalMinhaContaAberto(false)} />
    </>
  );
}
